import React from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { MaskInput } from '../../../common/mask-input';

import { BasicInput } from '../../../common/basic-input';
import { SecondaryButton } from '../../../common/secondary-button';
import { Select } from '../../../common/select';
import { PhoneStore } from '../../../stores/PhoneStore';
import { Validator } from '../../../helpers/Validator';
import styles from './PhoneSelection.css';

const PHONES = {
  FIRST: 'first',
  SECOND: 'second',
};

const NEW_PHONE_TEXT = '+ Nuevo teléfono';
const NEW_PHONE_ID = -1;

const IPropTypes = {
  onPhoneSelection: PropTypes.func,
  requestOnePhone: PropTypes.bool,
};

const defaultProps = {
  onPhoneSelection: () => {},
  requestOnePhone: false,
};

class PhoneSelection extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      creationModeEnabled: false,
      firstPhone: null,
      newPhoneValue: null,
      phoneToCreate: null,
      secondPhone: null,
      error: null,
    };

    this._createPhone = this._createPhone.bind(this);
    this._updateFirstPhone = this._updateFirstPhone.bind(this);
    this._updateNewPhone = this._updateNewPhone.bind(this);
    this._updateSecondPhone = this._updateSecondPhone.bind(this);
  }

  componentDidMount() {
    PhoneStore.getPhones();
  }

  _updateFirstPhone(value) {
    // Value NEW_PHONE_ID indicates that we need to create a new phone
    if (value === NEW_PHONE_ID) {
      this.setState({
        creationModeEnabled: true,
        phoneToCreate: PHONES.FIRST,
      });
    } else {
      const phone = PhoneStore.phones.find(p => p.id === value);
      this.setState({
        firstPhone: phone,
        creationModeEnabled: false,
        newPhoneValue: null,
        phoneToCreate: null,
      });
      this.props.onPhoneSelection(phone, this.state.secondPhone);
    }
  }

  _updateSecondPhone(value) {
    // Value NEW_PHONE_ID indicates that we need to create a new phone
    if (value === NEW_PHONE_ID) {
      this.setState({
        creationModeEnabled: true,
        newPhoneValue: null,
        phoneToCreate: PHONES.SECOND,
      });
    } else {
      const phone = PhoneStore.phones.find(p => p.id === value);
      this.setState({
        secondPhone: phone,
        creationModeEnabled: false,
        phoneToCreate: null,
      });
      if (this.state.firstPhone) {
        this.props.onPhoneSelection(this.state.firstPhone, phone);
      }
    }
  }

  _updateNewPhone(event) {
    const { target: { value } } = event;
    this.setState({
      newPhoneValue: value,
    });
  }

  async _createPhone() {
    this.setState({
      error: null,
    });

    if (!Validator.isPhone(this.state.newPhoneValue)) {
      this.setState({
        error: 'El formato de número de teléfono es incorrecto',
      });
      return;
    }

    await PhoneStore.addPhone(this.state.newPhoneValue);
    if (!PhoneStore.createError) {
      this.setState((prevState) => {
        const newState = {
          creationModeEnabled: false,
          phoneToCreate: null,
        };
        const newPhone = PhoneStore.phones[PhoneStore.phones.length - 1];
        if (prevState.phoneToCreate === PHONES.FIRST) {
          newState.firstPhone = newPhone;
        } else {
          newState.secondPhone = newPhone;
        }
        return newState;
      }, () => {
        if (this.state.firstPhone) {
          this.props.onPhoneSelection(this.state.firstPhone, this.state.secondPhone);
        }
        if (this.props.requestOnePhone) {
          this.props.onPhoneSelection(this.state.firstPhone, this.state.secondPhone);
        }
      });
    } else {
      this.setState({ error: PhoneStore.errorMessage });
    }
  }

  render() {
    const firstPhoneOptions = PhoneStore.phones
      .filter(phone => phone.type === 1).map(phone => ({
        label: phone.number,
        value: phone.id,
      }));
    const secondPhoneOptions = PhoneStore.phones
      .filter(phone => !(this.state.firstPhone && phone.id === this.state.firstPhone.id))
      .map(phone => ({
        label: phone.number,
        value: phone.id,
      }));
    let firstPhoneValue = this.state.firstPhone ? this.state.firstPhone.number : 'Seleccionar';
    if (this.state.creationModeEnabled) {
      firstPhoneValue = NEW_PHONE_TEXT;
    }
    let secondPhoneValue = this.state.secondPhone ? this.state.secondPhone.number : 'Seleccionar';
    if (this.state.creationModeEnabled) {
      secondPhoneValue = NEW_PHONE_TEXT;
    }

    if (!this.state.creationModeEnabled) {
      firstPhoneOptions.push({
        label: NEW_PHONE_TEXT,
        value: NEW_PHONE_ID,
      });
    }

    if (!this.state.creationModeEnabled) {
      secondPhoneOptions.push({
        label: NEW_PHONE_TEXT,
        value: NEW_PHONE_ID,
      });
    }

    // const text = `Celular *
    // ${!this.props.requestOnePhone ? 'te enviaremos SMS para informarte de tu envío' : ''}`;
    const text = '';

    return (
      <div>
        {!this.props.requestOnePhone &&
          <h5 className={styles.label}>
            Teléfono de contacto *
          </h5>
        }
        <h5 className={styles.label}>
          {text}
        </h5>
        {
          (!this.state.creationModeEnabled || this.state.phoneToCreate === PHONES.FIRST) &&
          <Select
            className={styles.select}
            onChange={this._updateFirstPhone}
            options={firstPhoneOptions}
            optionClassName={styles.selectPhone}
            selectedClassName={styles.selectPhone}
            value={firstPhoneValue}
          />
        }
        {
          (!this.state.creationModeEnabled || this.state.phoneToCreate === PHONES.SECOND) &&
          <div>
            <h5 className={styles.label}>
              Otro teléfono
            </h5>
            <Select
              className={styles.select}
              onChange={this._updateSecondPhone}
              options={secondPhoneOptions}
              optionClassName={styles.selectPhone}
              selectedClassName={styles.selectPhone}
              value={secondPhoneValue}
            />
          </div>
        }
        {
          this.state.creationModeEnabled &&
          <div>
            {
              this.state.phoneToCreate === PHONES.FIRST ?
                <MaskInput
                  info="El primer télefono debe ser de tipo celular"
                  error={this.state.error}
                  className={styles.input}
                  placeholder="09#######"
                  mask="N11111111"
                  size="9"
                  onChange={this._updateNewPhone}
                  formatCharacters={{
                    N: {
                      validate(char) {
                        return char >= 0;
                      },
                    },
                  }}
                /> :
                <BasicInput
                  className={styles.input}
                  value={this.state.newPhoneValue}
                  label=""
                  placeholder="09####### o [2 o 4]#######"
                  name="newPhone"
                  onChange={this._updateNewPhone}
                  error={this.state.error}
                />
            }
            {this.state.error && <p>{this.state.error}</p>}
            <SecondaryButton
              className={styles.button}
              disabled={!this.state.newPhoneValue
                || (this.state.newPhoneValue && !this.state.newPhoneValue.trim())}
              onClick={this._createPhone}
            >
              Guardar
            </SecondaryButton>
          </div>
        }
      </div>
    );
  }
}

PhoneSelection.propTypes = IPropTypes;
PhoneSelection.defaultProps = defaultProps;

const ObservedPhoneSelection = observer(PhoneSelection);

export { ObservedPhoneSelection as PhoneSelection };



// WEBPACK FOOTER //
// ./src/pages/shipping-options/phone-selection/PhoneSelection.jsx